import { createEventData } from './eventData';
import type { ActivitySyncContext, CalendarSyncResult } from './types';

/**
 * Le tre funzioni di `useMicrosoftCalendar` passate dal chiamante — modulo puro,
 * speculare a `GoogleCalendarApi` (googleSync.ts). Firme verificate su
 * `src/hooks/deals/useMicrosoftCalendar.ts`: NESSUN parametro `calendarId` su
 * update/delete (a differenza di Google) — il target è sempre `targetUserId`
 * (il mailbox Graph su cui operare), mai un calendario.
 */
export interface MicrosoftCalendarApi {
  createEvent(
    calendarId: string | undefined,
    event: any,
    opts: { createTeamsMeeting: boolean; activityId: string; targetUserId: string }
  ): Promise<{ eventId?: string; joinUrl?: string } | null>;
  updateEvent(eventId: string, event: any, targetUserId?: string): Promise<any>;
  deleteEvent(eventId: string, targetUserId?: string): Promise<any>;
}

/** Fonte: LeadStatusUpdateDrawer.tsx:1634 (stesso pattern di googleSync.isAuthError). */
function isAuthError(error: any): boolean {
  const msg = error?.message || '';
  return msg.includes('403') || msg.includes('Forbidden') || msg.includes('non-2xx status code');
}

/**
 * Sincronizza (create/update/move) l'evento Outlook per un'attività, in modo puro:
 * riceve l'`api` (le funzioni di `useMicrosoftCalendar`) come parametro e non fa mai
 * `throw`.
 *
 * Fonti: insert `LeadStatusUpdateDrawer.tsx:1644-1679`, update/move `:1287-1356`.
 *
 * Differenze rilevanti rispetto a `syncGoogleEvent` (NON un porting 1:1, il provider
 * Microsoft ha una forma diversa):
 *
 * 1. **Nessuno swap organizer/commerciale.** Nella fonte, `meetOrganizer === 'commercial'`
 *    (che sceglie il calendario del commerciale invitato) è usato SOLO nel ramo Google
 *    (:1530). Il ramo Microsoft, sia insert che update/move, targetizza sempre
 *    l'ASSEGNATARIO (`msAssignedUserId = activityData.assigned_to || newAssignee`). Qui
 *    `targetUserId` è quindi sempre `ctx.assigneeId`, mai `ctx.organizer?.userId` —
 *    anche quando `ctx.organizer` è valorizzato (test dedicato).
 *
 * 2. **Move fedele alla fonte (:1299-1328).** A differenza della prima versione di
 *    questo modulo, il move è ORA implementato: `existing.assignedTo` (aggiunto a
 *    `ActivitySyncContext.existing` — ref PERSISTITO da `editingActivity.assigned_to`,
 *    MAI stato async tipo `previousAssignedTo` della fonte) viene confrontato con
 *    `ctx.assigneeId`. Se diversi e `existing.microsoftEventId` è presente: delete
 *    dalla mailbox del VECCHIO assegnatario (`existing.assignedTo`), poi create sul
 *    nuovo (la guardia `!settings.microsoft.enabled` sotto intercetta già il caso "il
 *    nuovo assegnatario non ha sync" prima di arrivare qui, facendo delete +
 *    `clearRefs: true` — fonte :1322-1327). Senza `existing.assignedTo` (caller che non
 *    lo valorizza) il modulo degrada all'update in place di prima (nessuna regressione
 *    per i chiamanti che non passano ancora questo campo).
 *
 * 3. **Guardie, in ordine (fedeli al fix #3 di Google, stesso perimetro):**
 *    - `!ctx.syncOptIn` → `skipped`, nessuna chiamata, evento intonso.
 *    - `!settings.microsoft.enabled` → SOLO qui, se `existing.microsoftEventId` è
 *      presente: delete (best-effort, fonte :1322-1327) + `clearRefs: true`. Il target
 *      della delete è `existing.assignedTo` se valorizzato (l'evento vive lì, non sul
 *      nuovo assegnatario senza sync), altrimenti `ctx.assigneeId` (caso "stesso
 *      assegnatario, sync appena disattivata"). Altrimenti `skipped` senza toccare nulla.
 *    - `settings.microsoft.enabled === true` ma `!settings.microsoft.calendarId` →
 *      `error` (fix round 1 del redesign: prima era uno `skipped` silenzioso), evento
 *      esistente intonso (mai una delete con calendario non configurato).
 *
 * 4. **Teams SOLO per `videochiamata` + `meetingProvider === 'microsoft_teams'` E
 *    NESSUN `meetLink` già presente** (fonte :1309, :1335 — `!editingActivity?.meet_link`):
 *    evita di generare un secondo meeting Teams quando l'attività ne ha già uno.
 *    `existing.meetLink` (nuovo campo, ref persistito) veicola questo dato. A differenza
 *    del Meet Google (gestito con `conference_data` costruito qui), l'attivazione Teams è
 *    un parametro (`createTeamsMeeting`) passato a `api.createEvent` — la edge function
 *    `microsoft-calendar-modify` genera il join URL server-side. Nessun
 *    `conference_data`/`requestId` locale, quindi nessun `Date.now`. Teams si attiva SOLO
 *    in creazione (fonte, hook: "l'attivazione Teams è irreversibile"): l'update non
 *    rigenera mai un nuovo `meetLink`.
 *
 * 5. **Attendees come array di email semplici**, non `{ email }[]` (a differenza di
 *    Google): il payload atteso da `createMicrosoftEvent`/`updateMicrosoftEvent`
 *    (calendarStore) è `event.attendees: string[]` (fonte :1655, :1297).
 *
 * 6. **Niente falso `'ok'`.** `updateMicrosoftEvent` (calendarStore) inghiotte il
 *    `not_found` di Graph e ritorna `null` senza lanciare (fonte, commento :1332
 *    "not_found tollerato nello store"): se l'update ritorna un valore falsy, il modulo
 *    NON dichiara `status: 'ok'` con un `eventId` ormai inesistente — ritorna
 *    `status: 'error'` con un messaggio esplicativo.
 *
 * 7. **Mai `throw`.** Tutta la costruzione dell'evento (che tocca `Date.toISOString()`
 *    dentro `createEventData`, può lanciare `RangeError` con una data invalida) vive
 *    dentro l'unico `try` insieme alle chiamate API (delete+create del move incluse:
 *    fedele alla fonte, dove anche il `deleteMicrosoftEvent` del move sta dentro lo
 *    stesso `try` del ramo Microsoft, :1295-1352) — stesso fix "CRITICAL" applicato a
 *    `syncGoogleEvent`. `'auth_error'` su 403/Forbidden/non-2xx, `'error'` sugli altri
 *    errori (incluso il falso `'ok'` del punto 6 e il calendario non configurato del
 *    punto 3), `'skipped'` per le guardie di opt-out/sync disattivata del punto 3
 *    (mai `deleteEvent` con `calendarId`/target non risolto).
 */
export async function syncMicrosoftEvent(
  ctx: ActivitySyncContext,
  api: MicrosoftCalendarApi
): Promise<CalendarSyncResult> {
  const { settings, existing } = ctx;

  // Opt-out per-attività: non tocchiamo mai un evento eventualmente esistente.
  if (!ctx.syncOptIn) {
    return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
  }

  // Perimetro stretto (fix #3, fedele al pattern Google): SOLO l'assegnatario senza
  // sync attiva innesca la pulizia dell'evento residuo — mai per opt-out o calendario
  // non configurato. Fonte del clear-ref: :1322-1327. Target della delete: la mailbox
  // dove l'evento VIVE REALMENTE (existing.assignedTo se noto, altrimenti l'assegnatario
  // corrente per compatibilità coi chiamanti che non passano ancora assignedTo).
  if (!settings.microsoft.enabled) {
    if (existing?.microsoftEventId) {
      const deleteTarget = existing.assignedTo || ctx.assigneeId;
      try {
        await api.deleteEvent(existing.microsoftEventId, deleteTarget);
      } catch {
        // best-effort: azzeriamo comunque i ref, l'evento verrà ripulito manualmente se la delete fallisce
      }
      return { status: 'skipped', eventId: null, calendarId: null, meetLink: null, clearRefs: true };
    }
    return { status: 'skipped', eventId: null, calendarId: null, meetLink: null };
  }

  if (!settings.microsoft.calendarId) {
    // Sync abilitata ma calendario non configurato: nessuna chiamata API, evento
    // esistente intonso. Fix round 1 del redesign drawer: NON più uno `skipped`
    // silenzioso ma un `error` con messaggio — è una configurazione incoerente e
    // l'utente deve saperlo (il toast di useActivitySave lo mostra). Difensivo: dopo il
    // gate `calendarId` di `resolveSyncSwitch` lo switch non è più attivabile in questo
    // stato, quindi il ramo si raggiunge solo per configurazioni cambiate sotto i piedi.
    return {
      status: 'error',
      eventId: null,
      calendarId: null,
      meetLink: null,
      error: 'Sync Outlook non completata: nessun calendario selezionato nelle impostazioni',
    };
  }

  try {
    // Fonte :1291/:1646: target sempre l'assegnatario, MAI l'organizer/commerciale
    // (quello swap esiste solo nel ramo Google della fonte).
    const targetUserId = ctx.assigneeId;
    const targetCalendarId = settings.microsoft.calendarId;

    // CRITICAL: costruzione dell'evento DENTRO il try — createEventData() chiama
    // Date.toISOString() e può lanciare RangeError su una data invalida.
    const eventData: any = createEventData(ctx);
    if (ctx.attendees.length > 0) {
      // Fonte :1297/:1655 — array di email semplici, non oggetti { email }.
      eventData.attendees = ctx.attendees;
    }

    // Fonte :1657, :1309/:1335 — Teams SOLO per videochiamata + provider microsoft_teams,
    // e SOLO se l'attività non ha già un meetLink (evita un secondo meeting Teams).
    const wantsTeams =
      ctx.type === 'videochiamata' && ctx.meetingProvider === 'microsoft_teams' && !ctx.existing?.meetLink;

    // Fonte :1299-1328 — move: assegnatario cambiato rispetto al ref persistito.
    const reassigned = !!existing?.assignedTo && existing.assignedTo !== ctx.assigneeId;

    if (existing?.microsoftEventId && reassigned) {
      // Delete dalla mailbox del VECCHIO assegnatario (fonte :1305), poi create sul nuovo
      // (siamo già oltre la guardia !settings.microsoft.enabled: il nuovo HA sync attiva).
      await api.deleteEvent(existing.microsoftEventId, existing.assignedTo as string);
      const created = await api.createEvent(targetCalendarId, eventData, {
        createTeamsMeeting: wantsTeams,
        activityId: ctx.activityId,
        targetUserId,
      });
      return {
        status: 'ok',
        eventId: created?.eventId || null,
        calendarId: targetCalendarId,
        meetLink: wantsTeams ? created?.joinUrl || null : null,
      };
    }

    if (existing?.microsoftEventId) {
      // Fonte :1331-1333 — ref già presente, stesso assegnatario: update in place,
      // nessun nuovo Teams meeting.
      const updated = await api.updateEvent(existing.microsoftEventId, eventData, targetUserId);
      if (!updated) {
        // Niente falso 'ok': l'update store inghiotte il not_found di Graph e torna null.
        return {
          status: 'error',
          eventId: null,
          calendarId: null,
          meetLink: null,
          error: 'Microsoft Calendar event not found (not_found) — update ignorato dallo store',
        };
      }
      return {
        status: 'ok',
        eventId: existing.microsoftEventId,
        calendarId: targetCalendarId,
        meetLink: null,
      };
    }

    const created = await api.createEvent(targetCalendarId, eventData, {
      createTeamsMeeting: wantsTeams,
      activityId: ctx.activityId,
      targetUserId,
    });

    return {
      status: 'ok',
      eventId: created?.eventId || null,
      calendarId: targetCalendarId,
      meetLink: wantsTeams ? created?.joinUrl || null : null,
    };
  } catch (error: any) {
    return {
      status: isAuthError(error) ? 'auth_error' : 'error',
      eventId: null,
      calendarId: null,
      meetLink: null,
      error: error?.message || String(error),
    };
  }
}
